import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { cfpBaseUrl } from '../../network/urls';

/**
 * @typedef {Object} UsersState
 * @property {number} pageNumber - Current page number.
 * @property {number} pageSize - Number of records per page.
 * @property {number|null} totalRecords - Total number of records.
 * @property {number|null} totalPages - Total number of pages.
 * @property {Array} data - Array of user records.
 * @property {boolean} isLoading - Indicates whether the data is being loaded.
 * @property {string|null} error - Error message if the request fails.
 */

const initialState = {
    pageNumber: 1,
    pageSize: 20,
    totalRecords: null,
    searchParameter: null,
    totalPages: null,
    data: [],
    isLoading: false,
    error: null
}

/**
 * @function fetchPaginatedUsers
 * @description Async thunk that fetches paginated user data from the API.
 * @memberof Features
 * @async
 * @param {Object} params - Request parameters.
 * @param {number} params.pageNumber - The page number to fetch.
 * @param {number} params.pageSize - The number of records per page.
 * @param {Object} thunkAPI - The thunk API object.
 * @param {Function} thunkAPI.rejectWithValue - Function to return a rejected value in case of an error.
 * @param {Function} thunkAPI.getState - Function to get the current state.
 * @returns {Promise<Object>} A promise that resolves to user data or rejects with an error message.
 */
export const fetchPaginatedUsers = createAsyncThunk(
    'users/users',
    async ({ officeCode, pageNumber, pageSize, searchParameter }, { rejectWithValue, getState }) => {
        try {
            const { auth } = getState();
            const { token } = auth;

            const myHeaders = new Headers();
            myHeaders.append("Authorization", token);
            myHeaders.append("Content-Type", "application/json");

            const raw = JSON.stringify({
                pageNumber: pageNumber,
                pageSize: pageSize,
                officeCode: officeCode,
                searchParameter: searchParameter
            });

            const requestOptions = {
                method: "POST",
                headers: myHeaders,
                body: raw,
                redirect: "follow"
            };

            const response = await fetch(cfpBaseUrl + 'customerFaciliationPortal/getAllUsers', requestOptions);

            const data = await response.json();

            if (response.ok && data.status !== '200') {
                throw new Error(data.message || 'Actionable API issue!');
            }

            if (!response.ok) {
                throw new Error(data.message || 'Actionable API issue!');
            }
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

/**
* Redux slice for managing paginated user data.
 */
const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        nextPage(state) {
            state.pageNumber += 1;
        },
        previousPage(state) {
            if (state.pageNumber > 1) {
                state.pageNumber -= 1;
            }
        },
        firstPage(state) {
            state.pageNumber = 1;
        },
        lastPage(state, action) {
            state.pageNumber = action.payload;
        },
        resetState: (state) => {
            state.pageNumber = 1
            state.pageSize = 20
            state.totalRecords = null
            state.totalPages = null
            state.data = []
            state.isLoading = false
            state.error = null
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPaginatedUsers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchPaginatedUsers.fulfilled, (state, action) => {
                state.isLoading = false;
                if (action.payload.data) {
                    state.data = action.payload.data;
                } else {
                    state.data = [];
                }
                if (action.payload.totalPages) {
                    state.totalPages = action.payload.totalPages;
                } else {
                    state.totalPages = 1;
                }
                state.totalRecords = action.payload.totalRecords;
                state.error = null;
            })
            .addCase(fetchPaginatedUsers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { nextPage, previousPage, firstPage, lastPage, resetState } = usersSlice.actions;
export default usersSlice.reducer;